import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import styles from './TicketMarkdown.module.css';

// DataCentral stores ticket bodies as a hybrid of HTML (<p>, <br>) wrapping
// markdown (## headings, **bold**, lists). marked treats a leading <p> block as
// a raw HTML block and skips the markdown inside it, so the wrappers are
// normalized to plain newlines first; marked then renders the markdown, and the
// output is sanitized before injection.
function normalize(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?p[^>]*>/gi, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

marked.setOptions({ gfm: true, breaks: true });

interface TicketMarkdownProps {
  source: string;
  className?: string;
}

/** Renders a ticket body (description or reply) from DataCentral's markdown/HTML hybrid. */
export function TicketMarkdown({ source, className }: TicketMarkdownProps) {
  const html = useMemo(() => {
    const md = normalize(source);
    const rendered = marked.parse(md, { async: false }) as string;
    return DOMPurify.sanitize(rendered, { ADD_ATTR: ['target', 'rel'] });
  }, [source]);

  return (
    <div
      className={[styles.markdown, className].filter(Boolean).join(' ')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
